// src/pages/Admin.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { listStudents, createStudent, updateStudent, deleteStudent } from '../api'
import StudentFormModal from './StudentFormModal.jsx'
import ConfirmDialog from './ConfirmDialog.jsx'
import Toast from './Toast.jsx'
import Pagination from './Pagination.jsx'
import ChangePassword from './ChangePassword.jsx'
export default function Admin({ onLogout, theme, setTheme }){
  const [students, setStudents] = useState([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editData, setEditData] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [toast, setToast] = useState('')

  // profile / change password modal state
  const [profileOpen, setProfileOpen] = useState(false)
  const [showChangePwd, setShowChangePwd] = useState(false)

  // pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(8)

  async function refresh(){
    setLoading(true)
    try{
      const res = await listStudents()
      setStudents(res.data || [])
    }catch(e){ setToast(e.message) }
    finally{ setLoading(false) }
  }
  useEffect(()=>{ refresh() }, [])

  const filtered = useMemo(()=>{
    const q = query.trim().toLowerCase()
    if (!q) return students
    return students.filter(s =>
      [s.name, s.enrollment, s.course, s.session]
      .filter(Boolean)
      .some(x => x.toLowerCase().includes(q))
    )
  }, [students, query])

  const paginated = useMemo(()=>{
    const start = (page - 1) * pageSize
    return filtered.slice(start, start + pageSize)
  }, [filtered, page, pageSize])

  useEffect(()=>{ setPage(1) }, [query, pageSize]) // reset page on filter/page size change

  function onAdd(){ setEditData(null); setShowForm(true) }
  function onEdit(s){ setEditData(s); setShowForm(true) }

  async function onSave(form){
    try{
      if (editData){
        const fd = toFormData(form)
        await updateStudent(editData._id, fd)
        setToast('Student updated'); setShowForm(false); refresh()
      } else {
        const fd = toFormData(form)
        await createStudent(fd)
        setToast('Student created'); setShowForm(false); refresh()
      }
    }catch(e){ setToast(e.message) }
  }

  async function onDelete(){
    try{
      await deleteStudent(confirm._id)
      setToast('Student deleted'); setConfirm(null); refresh()
    }catch(e){ setToast(e.message) }
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="header" style={{position:'relative'}}>
        <div className="brand">
          <div className="logo">Student Admin</div>
          <span className="badge">v1</span>
        </div>

        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <select className="select" value={pageSize} onChange={e=> setPageSize(Number(e.target.value))}>
            <option value={5}>5 / page</option>
            <option value={8}>8 / page</option>
            <option value={12}>12 / page</option>
            <option value={20}>20 / page</option>
          </select>

          <button className="btn" onClick={onAdd}>Add Student</button>

          <button className="btn ghost" onClick={()=> setTheme(theme==='dark'?'light':'dark')}>
            {theme==='dark'?'Light Mode':'Dark Mode'}
          </button>

          {/* Profile button */}
          <div style={{position:'relative'}}>
            <button
              className="btn ghost"
              onClick={() => setProfileOpen(p => !p)}
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
                <span style={{
                  width:28, height:28, borderRadius:8, display:'inline-flex', alignItems:'center', justifyContent:'center',
                  background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'#fff', fontWeight:700
                }}>SA</span>
                <span style={{minWidth:56, textAlign:'left'}}>Admin</span>
                <span style={{opacity:0.7}}>▾</span>
              </span>
            </button>

            {profileOpen && (
              <div
                className="profile-menu"
                style={{
                  position:'absolute',
                  right:0,
                  top:46,
                  background: 'var(--card-bg, #fff)',
                  boxShadow:'0 10px 30px rgba(2,6,23,0.08)',
                  borderRadius:8,
                  overflow:'hidden',
                  zIndex:40,
                  minWidth:200
                }}
                onMouseLeave={() => setProfileOpen(false)}
              >
                <button
                  className="profile-item"
                  style={{display:'block', width:'100%', textAlign:'left', padding:'10px 12px', border:'none', background:'transparent'}}
                  onClick={() => { setShowChangePwd(true); setProfileOpen(false) }}
                >
                  🔒 Change Password
                </button>

                <button
                  className="profile-item"
                  style={{display:'block', width:'100%', textAlign:'left', padding:'10px 12px', border:'none', background:'transparent'}}
                  onClick={() => { setProfileOpen(false); onLogout && onLogout() }}
                >
                  ↩️ Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search / results */}
      <div className="card" style={{marginTop:18}}>
        <div className="grid" style={{gridTemplateColumns:'1fr 220px', alignItems:'center'}}>
          <input className="input" placeholder="Search by name, enrollment, course, session…" value={query} onChange={e=>setQuery(e.target.value)}/>
          <div style={{textAlign:'right', color:'var(--muted)'}}>{filtered.length} result(s)</div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{marginTop:18}}>
        {loading ? (
          <div style={{padding:24, color:'var(--muted)'}}>Loading…</div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Enrollment</th>
                  <th>Name</th>
                  <th>Course / Session</th>
                  <th>Remark</th>
                  <th>Result</th>
                  <th style={{textAlign:'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(s => (
                  <tr key={s._id}>
                    <td>
                      <img src={s.photoUrl} alt="" style={{width:44, height:44, borderRadius:8, objectFit:'cover'}}/>
                    </td>
                    <td>{s.enrollment}</td>
                    <td>
                      <div style={{fontWeight:600}}>{s.name}</div>
                      <div style={{fontSize:12, color:'var(--muted)'}}>DOB: {s.dob}</div>
                    </td>
                    <td>
                      <div>{s.course}</div>
                      <div style={{fontSize:12, color:'var(--muted)'}}>{s.session || '-'}</div>
                    </td>
                    <td>{s.result?.remark || '-'}</td>
                    <td>
                      {s.result?.hasFile ? (
                        <a className="btn secondary" href={s.result.downloadLink} target="_self" rel="noreferrer">Download</a>
                      ) : <span style={{color:'var(--muted)'}}>No file</span>}
                    </td>
                    <td style={{textAlign:'right'}}>
                      <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                        <button className="btn secondary" onClick={()=>onEdit(s)}>Edit</button>
                        <button className="btn danger" onClick={()=>setConfirm(s)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr><td colSpan="7" style={{padding:18, color:'var(--muted)'}}>No students found.</td></tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Student form modal */}
      {showForm && (
        <StudentFormModal
          initial={editData}
          onCancel={()=> setShowForm(false)}
          onSave={onSave}
        />
      )}

      {/* Confirm delete dialog */}
      {confirm && (
        <ConfirmDialog
          title="Delete Student"
          message={`Are you sure you want to delete ${confirm.name} (${confirm.enrollment})? This cannot be undone.`}
          confirmText="Delete"
          onCancel={()=> setConfirm(null)}
          onConfirm={onDelete}
        />
      )}

      {/* Change Password modal (opened from header) */}
      {showChangePwd && (
        <div className="modal-backdrop" onClick={() => setShowChangePwd(false)} style={{
          position:'fixed', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(2,6,23,0.45)', zIndex:60
        }}>
          <div className="modal-card" onClick={(e)=>e.stopPropagation()} style={{width:560, maxWidth:'95%', borderRadius:12, padding:16, background:'var(--card-bg, #fff)'}}>
            <button className="modal-close" onClick={() => setShowChangePwd(false)} style={{position:'absolute', right:12, top:12, border:'none', background:'transparent', fontSize:16}}>✕</button>
            <ChangePassword theme={theme} setTheme={setTheme} />
          </div>
        </div>
      )}

      {toast && <Toast onClose={()=> setToast('')}>{toast}</Toast>}
    </div>
  )
}

function toFormData(form){
  const fd = new FormData()
  ;['enrollment','name','fatherName','motherName','session','course','dob','remark'].forEach(k=>{
    if (form[k] !== undefined && form[k] !== null) fd.append(k, form[k])
  })
  if (form.studentPhoto) fd.append('studentPhoto', form.studentPhoto)
  if (form.resultFile) fd.append('resultFile', form.resultFile)
  return fd
}
